import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Package, Check, AlertTriangle, Clock, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const RawMaterialUsage = () => {
  const navigate = useNavigate();
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [itemNotes, setItemNotes] = useState('');
  const [verifiedBy, setVerifiedBy] = useState('');
  const [overallNotes, setOverallNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchRawMaterials();
  }, []);

  const fetchRawMaterials = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/raw-materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRawMaterials(response.data);
    } catch (err) {
      console.error('Error fetching raw materials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    if (!selectedProduct || !quantity || parseFloat(quantity) <= 0) {
      setError('Please select a product and enter a valid quantity');
      return;
    }

    const product = rawMaterials.find(p => p.id === selectedProduct);
    if (!product) return;

    // Check if already added
    if (items.find(i => i.product_id === selectedProduct)) {
      setError('This product is already in the list');
      return;
    }

    // Check stock availability
    if (parseFloat(quantity) > product.current_stock) {
      setError(`Insufficient stock. Available: ${product.current_stock}`);
      return;
    }

    setItems([...items, {
      product_id: product.id,
      product_name: product.name,
      quantity: parseFloat(quantity),
      current_stock: product.current_stock,
      notes: itemNotes
    }]);

    // Reset inputs
    setSelectedProduct('');
    setQuantity('');
    setItemNotes('');
    setError('');
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    if (!verifiedBy.trim()) {
      setError('Please enter verified by name');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/raw-material-usage`, {
        items: items.map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          quantity: i.quantity,
          notes: i.notes
        })),
        verified_by: verifiedBy,
        overall_notes: overallNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Raw material usage recorded successfully!');
      setItems([]);
      setVerifiedBy('');
      setOverallNotes('');
      fetchRawMaterials(); // Refresh stock
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to record usage');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredMaterials = rawMaterials.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.sku?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="raw-material-usage-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/gposp/dashboard')}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Raw Material Usage</h1>
            <p className="text-slate-500">Record usage of raw materials for production</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/gposp/raw-material-movement')}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl hover:bg-violet-700"
          >
            <Package size={18} />
            Movement Report
          </button>
          <button
            onClick={() => navigate('/gposp/raw-material-history')}
            className="flex items-center gap-2 px-4 py-2 text-violet-600 border border-violet-200 rounded-xl hover:bg-violet-50"
          >
            <Clock size={18} />
            Usage History
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
          <AlertTriangle size={20} />
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 flex items-center gap-2">
          <Check size={20} />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Add Item Section */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h2 className="font-semibold flex items-center gap-2">
              <Package size={20} className="text-violet-600" />
              Add Raw Material
            </h2>
          </div>
          <div className="p-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search raw materials..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>

            {/* Product Select */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Select Product <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="product-select"
              >
                <option value="">-- Select Raw Material --</option>
                {filteredMaterials.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name} (Stock: {m.current_stock})
                  </option>
                ))}
              </select>
              {rawMaterials.length === 0 && (
                <p className="mt-2 text-sm text-amber-600">
                  No raw materials found. Mark products as "Raw Material (Not for Sale)" in Products page.
                </p>
              )}
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Quantity Used <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Enter quantity"
                min="0.01"
                step="0.01"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="quantity-input"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes (Optional)</label>
              <input
                type="text"
                value={itemNotes}
                onChange={(e) => setItemNotes(e.target.value)}
                placeholder="e.g., Used for preparation"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>

            <button
              onClick={handleAddItem}
              disabled={!selectedProduct || !quantity}
              className="w-full flex items-center justify-center gap-2 py-3 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-item-btn"
            >
              <Plus size={20} />
              Add to List
            </button>
          </div>
        </div>

        {/* Items Grid & Submit */}
        <div className="space-y-4">
          {/* Items Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-semibold">Usage Items ({items.length})</h2>
            </div>
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Package className="mx-auto mb-2 text-slate-300" size={48} />
                <p>No items added yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Product</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Qty</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">Stock</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((item, index) => (
                      <tr key={index} data-testid={`usage-item-${index}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-sm">{item.product_name}</p>
                          {item.notes && <p className="text-xs text-slate-500">{item.notes}</p>}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-violet-600">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-slate-500">
                          {item.current_stock} → {item.current_stock - item.quantity}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleRemoveItem(index)}
                            className="p-1 hover:bg-red-50 text-red-500 rounded-lg"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Submit Section */}
          {items.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Verified By <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={verifiedBy}
                  onChange={(e) => setVerifiedBy(e.target.value)}
                  placeholder="Enter name of verifier"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="verified-by-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Overall Notes (Optional)</label>
                <textarea
                  value={overallNotes}
                  onChange={(e) => setOverallNotes(e.target.value)}
                  rows={2}
                  placeholder="Any additional notes..."
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={submitting || !verifiedBy.trim()}
                className="w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50"
                data-testid="submit-usage-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Usage & Deduct Stock'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RawMaterialUsage;
